import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';

// CONFIGS

// INTERFACES
import { takeUntil } from 'rxjs/operators';

import { trackByValue, trackById } from '../../../shared/util/track-by.util';
import { ITagOld } from '../../../tag/interfaces';

// SERVICES
import { TagFacadeService } from '../../../tag/tag-facade.service';
import { ChannelFacadeService } from '../../channel-facade.service';

// UTILS

@Component({
  selector: 'lum-channel-filters',
  templateUrl: './channel-filters.component.html',
  styleUrls: ['./channel-filters.component.scss'],
})
export class ChannelFiltersComponent implements OnInit, OnDestroy {
  @ViewChild('tagInput')
  tagInput :ElementRef<HTMLInputElement>;

  filterOptions :{
    [key :string] :string | boolean;
  }[] = [
    {
      label: 'Channel',
      checked: true,
      disabled: true,
    },
    {
      label: 'Status',
      checked: true,
      disabled: false,
    },
    {
      label: 'Tags',
      checked: false,
      disabled: false,
    },
  ];

  separatorKeysCodes :number[] = [ENTER, COMMA];
  tagOptionFormatted :any[] = [];

  private onDestroy$ = new Subject();

  trackByValue = trackByValue;
  trackById = trackById;

  constructor(
    private channelFacadeService :ChannelFacadeService,
    private tagFacadeService :TagFacadeService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.loadTags();
    this.subscribeToTags();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSelectTag(event :{ tag :number[] }) :void {
    this.onFilterChange(event.tag.join(',').toString(), 'tagIds');
  }

  onFilterChange(searchStr :any, property :string) :void {
    const queryParams = {
      ...this.currentQueryParams,
      [property]: searchStr.toString(),
    };
    this.channelFacadeService.setCurrentQueryParams(queryParams);
  }

  private loadTags() :void {
    this.tagFacadeService
      .getTags({ params: this.tagQueryParams })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe();
  }

  private subscribeToTags() :void {
    this.tags$.pipe(takeUntil(this.onDestroy$)).subscribe((tags :ITagOld[]) => {
      const traverse = (items :any, level :any, disabled = false) => {
        level += 1;
        const whitespacePrefix = '  '.repeat(level);

        items.children.forEach((element) => {
          // Directly check if the tag should be disabled

          this.tagOptionFormatted.push({
            level,
            ...element,
            name: whitespacePrefix + element.name,
            disabled: false,
            rawName: element.name,
          });

          if (element.children.length > 0) {
            traverse(element, level, false);
          }
        });
      };

      tags.forEach((element :ITagOld) => {
        const level = 0;
        // Directly check if the tag should be disabled for the top-level element

        this.tagOptionFormatted.push({
          level,
          ...element,
          disabled: false,
          rawName: element.name,
        });

        if (element.children.length > 0) {
          traverse(element, level, false);
        }
      });
    });
  }

  get currentQueryParams() :{ [param :string] :string | string[] } {
    return this.channelFacadeService.currentQueryParams;
  }

  get tagQueryParams() :{ [param :string] :string | string[] } {
    return this.tagFacadeService.currentQueryParams;
  }

  get tags$() :Observable<ITagOld[]> {
    return this.tagFacadeService.tags$;
  }

  get selectedTags$() :Observable<ITagOld[]> {
    return this.tagFacadeService.selectedTags$;
  }

  get isShowFilterTags() :boolean {
    // Find filter option by label
    const filterOption = this.filterOptions.find(
      (option) => option.label === 'Tags',
    );

    return filterOption.checked as boolean;
  }

  get isShowFilterStatus() :boolean {
    // Find filter option by label
    const filterOption = this.filterOptions.find(
      (option) => option.label === 'Status',
    );

    return filterOption.checked as boolean;
  }
}
